import { memo, useContext, useEffect, useMemo, useState } from 'react'
import { Close } from '@icon-park/react'

import { Tokens } from '@/assets/tokens'
import { Button, Flex, Form, Input, Selector } from '@/components'
import { HorizontalTagInput } from '@/components/core/HorizontalTagInput/HorizontalTagInput.component'
import { TagOption } from '@/components/core/HorizontalTagInput/HorizontalTagInput.hooks'
import { SelectionOption } from '@/components/core/Selector'
import { useInput, useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { DialogContext } from '@/modules/dialog/Dialog.context'
import { CreateSpokespersonRequest, SpokespersonImportResult } from '@shared/models'
import { Str } from '@/utils/strings'

import { useCompanyContext } from '../../../CompanyDetails.context'
import { ExcelUpload } from '../uploader/ExcelUploader'
import { Divider } from '../uploader/ExcelUploader.styles'
import { DialogContainer, DialogOverlay, ModalBody, ModalHeaderContainer } from './AddSpokespersonFormModal.style'

type Props = {
  onSuccess: () => void
}

function ImportResultContent({ total, created, updated, errors }: SpokespersonImportResult): JSX.Element {
  return (
    <Flex $type="column" $align="stretch" $gap={12}>
      <Flex $type="row" $align="center" $gap={24}>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#666' }}>{total}</span>
          <span style={{ fontSize: 13, color: '#666' }}>linhas</span>
        </Flex>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#1D9E75' }}>{created}</span>
          <span style={{ fontSize: 13, color: '#666' }}>criados</span>
        </Flex>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#185FA5' }}>{updated}</span>
          <span style={{ fontSize: 13, color: '#666' }}>atualizados</span>
        </Flex>
        {errors.length > 0 && (
          <Flex $type="column" $align="center" $gap={4}>
            <span style={{ fontSize: 28, fontWeight: 500, color: '#D85A30' }}>{errors.length}</span>
            <span style={{ fontSize: 13, color: '#666' }}>erros</span>
          </Flex>
        )}
      </Flex>
      {errors.length > 0 && (
        <Flex $type="column" $align="stretch" $gap={6} style={{ marginTop: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#D85A30' }}>Erros encontrados:</span>
          {errors.map((err, i) => (
            <span
              key={i}
              style={{
                fontSize: 12,
                color: '#666',
                padding: '4px 8px',
                background: '#fff5f5',
                borderRadius: 6,
              }}>
              {err}
            </span>
          ))}
        </Flex>
      )}
    </Flex>
  )
}

export const AddSpokespersonFormModal = memo(Component)

function Component({ onSuccess }: Props): JSX.Element | null {
  const { companyID, changeQueryParam, currentPage } = useRouting()
  const { company, allBrands, reloadAllBrands, reloadSpokesPeople } = useCompanyContext()
  const { sendAlert } = Alert.Hook()
  const dialog = useContext(DialogContext)

  const [excelFile, setExcelFile] = useState<File | null>(null)

  const { value: name, binder: nameBinder, reset: resetName } = useInput()
  const {
    binder: scoringBrandsBinder,
    selectedOptions: selectedScoringBrands,
    reset: resetScoringBrands,
  } = Selector.MultiBinder()
  const {
    binder: cenaBrandsBinder,
    selectedOptions: selectedCenaBrands,
    reset: resetCenaBrands,
  } = Selector.MultiBinder()
  const [variations, setVariations] = useState<TagOption[]>([])

  const brandOptions = useMemo<SelectionOption[]>(() => {
    return allBrands.map((brand) => ({
      text: brand.name,
      value: brand.id,
    }))
  }, [allBrands])

  useEffect(() => {
    if (!allBrands.length) {
      reloadAllBrands()
    }
  }, [allBrands.length, reloadAllBrands])

  useEffect(() => {
    const allowed = new Set(selectedScoringBrands.map((item) => String(item.value)))
    const filteredCenaBrands = selectedCenaBrands.filter((item) => allowed.has(String(item.value)))
    if (filteredCenaBrands.length !== selectedCenaBrands.length) {
      resetCenaBrands(filteredCenaBrands)
    }
  }, [selectedScoringBrands, selectedCenaBrands, resetCenaBrands])

  const { isRunning: isCreatingSpokesperson, run: createSpokesperson } = Api.Command<void>(
    Api.Methods.POST,
    `spokespersons`,
  )
  const { isRunning: isImportingSpokespersons, run: importSpokespersons } = Api.Command<SpokespersonImportResult>(
    Api.Methods.POST,
    `spokespersons/${companyID}/import`,
  )

  function handleClose(): void {
    changeQueryParam({ param: 'addSpokesperson', value: undefined })
  }

  function resetForm(): void {
    resetName()
    resetScoringBrands([])
    resetCenaBrands([])
    setVariations([])
  }

  function handleExcelImport(): void {
    if (!excelFile) return

    const formData = new FormData()
    formData.append('file', excelFile)

    importSpokespersons<FormData>(
      {
        input: formData,
        onSuccess: (result: SpokespersonImportResult) => {
          setExcelFile(null)
          reloadSpokesPeople(currentPage)
          dialog?.setData({
            title: 'Resultado da Importação',
            content: <ImportResultContent {...result} />,
            closeText: 'Fechar',
            customActions: (
              <Button.Root $variant="outlined" onClick={() => dialog?.close()}>
                <Button.Text value="Fechar" />
              </Button.Root>
            ),
            justifiedButtons: true,
          })
          dialog?.open()
        },
      },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  }

  function onSubmit(): void {
    if (Str.from(name).isBlank) {
      return sendAlert({ type: 'error', title: 'Campo obrigatório', message: 'Nome é obrigatório' })
    }

    if (!selectedScoringBrands.length) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Selecione ao menos uma marca para pontuação',
      })
    }

    const input = {
      companyID,
      name,
      scoringBrandIDs: selectedScoringBrands.map((option) => String(option.value)),
      cenaBrandIDs: selectedCenaBrands.map((option) => String(option.value)),
      variations: variations.map((tag) => tag.text),
    } as CreateSpokespersonRequest

    createSpokesperson<CreateSpokespersonRequest>({
      input,
      onSuccess: () => {
        sendAlert({ type: 'success', title: 'Sucesso', message: 'Porta-voz cadastrado com sucesso!' })
        resetForm()
        reloadSpokesPeople(currentPage)
        onSuccess()
      },
    })
  }

  if (!companyID || !company) return null

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Adicionar Porta-voz</h1>
          <Button.Root $variant="link" type="button" onClick={handleClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <ModalBody>
          <Flex $type="column" $align="stretch" $gap={20} $fit>
            <Form onSubmit={onSubmit} controlParam={isCreatingSpokesperson}>
              <Input label="Nome" placeholder="Nome" {...nameBinder} required />
              <Selector.Multi
                binder={scoringBrandsBinder}
                label="Marcas"
                options={brandOptions}
                placeholder="Selecione uma ou mais marcas"
                required
                $fit
              />
              <Selector.Multi
                binder={cenaBrandsBinder}
                label="CENA"
                options={selectedScoringBrands}
                placeholder="Selecione marcas (subconjunto das marcas de pontuação)"
                $fit
              />
              <HorizontalTagInput
                label="Variações"
                placeholder="Digite variações separadas por ponto e vírgula (;)"
                maxCharacters={200}
                preSelectedTags={variations}
                onChange={setVariations}
                disabled={isCreatingSpokesperson}
              />
              <Flex $type="row" $justify="flex-end" $fit>
                <Button.Root type="submit" $variant="primary" $size={228} $loading={isCreatingSpokesperson}>
                  <Button.Text value="Adicionar" />
                </Button.Root>
              </Flex>
            </Form>

            <Divider>ou importe uma planilha</Divider>

            <ExcelUpload
              file={excelFile}
              onFileSelect={setExcelFile}
              onImport={handleExcelImport}
              isImporting={isImportingSpokespersons}
              acceptedFormats={['.xls', '.xlsx']}
            />
          </Flex>
        </ModalBody>
      </DialogContainer>
    </DialogOverlay>
  )
}
